import { useState } from 'react';
import { Modal, prefsStore, usePrefsValue } from '@adiox/ui';
import { sizeOf, PREFS_LIMIT_BYTES } from '../chart/templates';
import { cleanProfileName, normalizeProfiles, profileNameProblem, MAX_PROFILES } from './profiles';

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';
const small = 'rounded border border-line px-2 py-0.5 text-xs hover:border-accent';

/**
 * MT5's profiles: save the whole workspace (panels, charts and their settings) under a name and switch between them. They
 * are kept with the synced preferences, so they follow the user to another browser; up to five, within the size limit.
 */
export function ProfilesDialog({
  open,
  onClose,
  current,
  apply,
}: {
  open: boolean;
  onClose: () => void;
  /** The layout as it is now. */
  current: () => unknown;
  /** Replaces the workspace with a saved layout; false when it could not be used. */
  apply: (layout: unknown) => boolean;
}) {
  const raw = usePrefsValue((s) => s.data.profiles);
  const profiles = normalizeProfiles(raw);
  const [name, setName] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const save = () => {
    const problem = profileNameProblem(name, profiles);
    if (problem) return setMessage({ tone: 'error', text: problem });
    const clean = cleanProfileName(name);
    const next = { ...profiles, [clean]: { layout: current(), savedAt: new Date().toISOString() } };
    if (sizeOf({ ...prefsStore.getState().data, profiles: next }) > PREFS_LIMIT_BYTES) {
      return setMessage({
        tone: 'error',
        text: 'Your saved settings are full. Delete a profile, some drawings or a chart template first.',
      });
    }
    prefsStore.set('profiles', next);
    setName('');
    setMessage({ tone: 'ok', text: `Saved “${clean}”.` });
  };

  const remove = (key: string) => {
    const rest = { ...profiles };
    delete rest[key];
    prefsStore.set('profiles', rest);
  };

  return (
    <Modal size="md" open={open} title="Profiles" onClose={onClose}>
      <div className="flex flex-col gap-3 text-sm">
        <p className="text-muted">
          A profile keeps the whole workspace: which panels are open and where, and every chart with its
          symbol, timeframe, indicators and settings. Drawings and templates are shared by all profiles.
        </p>
        {Object.keys(profiles).length === 0 ? (
          <p className="text-muted">No profiles yet.</p>
        ) : (
          <ul className="flex flex-col gap-1" aria-label="Saved profiles">
            {Object.entries(profiles).map(([key, p]) => (
              <li
                key={key}
                className="flex items-center gap-2 border-b border-line/40 py-1"
                data-testid="profile-row"
              >
                <span className="min-w-0 flex-1 truncate font-semibold">{key}</span>
                <span className="text-xs text-muted">{p.savedAt.slice(0, 10)}</span>
                <button
                  type="button"
                  className={small}
                  onClick={() => {
                    if (apply(p.layout)) onClose();
                    else
                      setMessage({
                        tone: 'error',
                        text: `“${key}” could not be opened: it was saved by an older version.`,
                      });
                  }}
                >
                  Open
                </button>
                <button
                  type="button"
                  className={`${small} text-down`}
                  aria-label={`Delete ${key}`}
                  onClick={() => remove(key)}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <div className="flex-1">
            <label className="mb-1 block text-xs uppercase tracking-wide text-muted" htmlFor="profile-name">
              Save the current workspace as (up to {MAX_PROFILES})
            </label>
            <input
              id="profile-name"
              className={field}
              maxLength={30}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button type="submit" className="rounded-md bg-accent px-3 py-1 font-semibold text-accent-fg">
            Save
          </button>
        </form>
        {message && (
          <p
            role={message.tone === 'error' ? 'alert' : 'status'}
            className={message.tone === 'error' ? 'text-down' : 'text-up'}
          >
            {message.text}
          </p>
        )}
      </div>
    </Modal>
  );
}
